<template>
  <div v-if="loading" class="page-loading">
    <Loading />
  </div>
  <div
    v-if="isBack && isBackVisible"
    @click="handleBack"
    class="icon-button-container"
  >
    <span class="material-symbols-outlined">arrow_back</span>
    {{ $t("Custom.back") }}
  </div>
  <div v-if="isDeveloperInfoVisible">
    <div class="properties-strip">
      <div class="action-strip-buttons">
        <div
          class="dropdown"
          :class="{ open: isListMenuOpen }"
          ref="listDropdown"
        >
          <div class="caption" @click="toggleDropdown">
            <span class="material-icons-outlined list-icon">
              playlist_add
            </span>
            {{ $t("List.addToList") }}
          </div>
          <div class="list">
            <div
              v-for="item in this.developerList"
              :key="item.listId"
              class="checkbox-item"
              :title="item.list.listName"
            >
              <div class="list-item">
                <input
                  type="checkbox"
                  :id="`list-${item.id}`"
                  :checked="isChecked(item.listId)"
                  @change="setList(item.listId, $event.target.checked)"
                />
                <label :for="`list-${item.listId}`">{{
                  item.list.listName
                }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="action-handle-buttons">
          <template v-if="developerFromLibrary">
            <div
              class="icon-button-container"
              @click="handleAreYouSureDeveloperModal"
              title="Remove From Library"
            >
              <template v-if="libraryLoading">
                <span class="button-spinner"></span>
              </template>
              <template v-else>
                <span
                  class="material-symbols-outlined icon"
                  style="color: green"
                >
                  bookmarks
                </span>
                <span style="margin-left: 5px; color: green"> Added </span>
              </template>
            </div>
          </template>
          <template v-else>
            <div
              class="icon-button-container"
              @click="handleAddDeveloperToLibrary"
              title="Add To Library"
            >
              <template v-if="libraryLoading">
                <span class="button-spinner"></span>
              </template>
              <template v-else>
                <span class="material-symbols-outlined icon" style="color: red">
                  bookmark_add
                </span>
                <span style="margin-left: 5px; color: red">
                  Add To Library
                </span>
              </template>
            </div>
          </template>
          <div class="icon-button-container" @click="updateNotification">
            <template v-if="isNotificationLoading">
              <span class="button-spinner"></span>
            </template>
            <template v-else>
              <span
                class="material-symbols-outlined icon"
                :style="
                  developerFromLibrary?.isNotification
                    ? { color: 'green' }
                    : { color: 'red' }
                "
              >
                {{
                  developerFromLibrary?.isNotification
                    ? "notifications_active"
                    : "notification_add"
                }}
              </span>
              <span
                style="margin-left: 5px"
                :style="
                  developerFromLibrary?.isNotification
                    ? { color: 'green' }
                    : { color: 'red' }
                "
              >
                {{ developerFromLibrary?.isNotification ? "Is On" : "Is Off" }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="developer-wrapper">
      <div
        v-if="developerData.pageCover"
        class="developer-cover"
        :style="{ backgroundImage: `url(${developerData.pageCover})` }"
      >
        <div class="cover-overlay"></div>
      </div>
      <div class="developer-header">
        <div class="developer-container">
          <div class="developer-main-info">
            <div class="developer-icon" v-if="developerData.icon">
              <img :src="developerData.icon" :alt="name" />
            </div>
            <div v-else class="developer-icon no-icon">
              <span class="material-symbols-outlined"> store </span>
            </div>
            <div class="developer-primary">
              <h1 class="developer-name">{{ developerData.name }}</h1>
              <div class="badges-container">
                <div>
                  <a
                    :href="developerData.url"
                    target="_blank"
                    rel="noopener"
                    class="platform-badge"
                    :class="developerData.platformType.name.toLowerCase()"
                  >
                    <img
                      :src="getStoreImage(developerData.platformType.id)"
                      :alt="developerData.platformType"
                      class="platform-icon"
                    />
                    {{ developerData.platformType.name }}
                  </a>
                </div>
                <div class="apps-badge">
                  <span
                    class="material-symbols-outlined"
                    style="margin-right: 5px"
                    >newsstand</span
                  >
                  {{ developerData.appCount }} {{ $t("Apps.app") }}
                </div>
              </div>
            </div>
          </div>

          <div class="developer-links">
            <a
              v-if="developerData.supportWebsite"
              :href="developerData.supportWebsite"
              target="_blank"
              class="link-item store-link"
            >
              <span class="material-symbols-outlined">language</span>
              <span>{{ $t("Developers.website") }}</span>
            </a>
          </div>

          <div class="developer-support" v-if="hasSupport">
            <h2 class="support-title">
              {{ $t("Developers.supportInformation") }}
            </h2>

            <div class="support-grid">
              <div class="support-item" v-if="developerData.supportEmail">
                <span class="material-symbols-outlined">mail</span>
                <a :href="'mailto:' + developerData.supportEmail">{{
                  developerData.supportEmail
                }}</a>
              </div>

              <div class="support-item" v-if="developerData.supportPhoneNumber">
                <span class="material-symbols-outlined">phone</span>
                <a :href="'tel:' + developerData.supportPhoneNumber">{{
                  developerData.supportPhoneNumber
                }}</a>
              </div>

              <div
                class="support-item address"
                v-if="developerData.supportAddressText"
              >
                <span class="material-symbols-outlined">location_on</span>
                <div class="address-content">
                  <span>{{ developerData.supportAddressText }}</span>
                  <a
                    v-if="developerData.supportAddressMapLink"
                    :href="developerData.supportAddressMapLink"
                    target="_blank"
                    class="map-link"
                  >
                    {{ $t("Developers.viewOnMap") }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="developer-note-container">
            <div class="note-icon-area" @click="hanldeNoteModal">
              <span class="material-symbols-outlined">description</span>
              <span class="note-preview">
                {{
                  developerFromLibrary?.note
                    ? developerFromLibrary.note.substring(0, 40) + "..."
                    : $t("Developers.addNote")
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="filter-container">
      <button class="import-btn" @click.stop="importTo">
        <span class="material-symbols-outlined"> upload </span>
        Import
      </button>
      <FilterComponent
        :sortCriteria="sortCriteria"
        :sortOrder="sortOrder"
        :sortingOptions="sortingOptions"
        :orderOptions="orderOptions"
        :isOrderOptions="true"
        @filter-change="handleFilterChange"
      />
    </div>
    <div class="list-items-content">
      <TableComponent>
        <template v-slot:table-header>
          <tr>
            <th v-for="header in tableHeaders" :key="header.key">
              {{ header.label }}
            </th>
          </tr>
        </template>
        <template v-slot:table-body>
          <tr
            v-for="(item, index) in filteredAndSortedItems"
            :key="item.id"
            :class="{ 'zebra-stripe': index % 2 === 0 }"
          >
            <td>{{ index + 1 }}</td>
            <td>
              <img
                :src="getItemIcon(item.icon)"
                alt="App Icon"
                class="app-icon"
              />
            </td>
            <td>
              <a :href="item.link" target="_blank">
                <img
                  :src="getStoreImage(item.platformTypeId)"
                  alt="Platform Icon"
                  class="platform-icon"
                  title="Show App On Store"
                />
              </a>
            </td>
            <td>
              <a
                class="clickable-text"
                :href="
                  $router.resolve({
                    name: 'App',
                    params: { storeId: item.storeId },
                  }).href
                "
                @click.stop="
                  handleLinkClick($event, {
                    name: 'App',
                    params: { storeId: item.storeId },
                  })
                "
                v-html="item?.name || 'N/A'"
              >
              </a>
            </td>

            <td style="cursor: pointer; font-weight: bold">
              {{ item?.developer?.name || "" }}
            </td>
            <td>{{ formatDate(item.lastUpdated) || "N/A" }}</td>
            <td>{{ formatDate(item.relasedDate) || "N/A" }}</td>
            <td>{{ item.price || "N/A" }}</td>
            <td>{{ item.rate || "N/A" }}</td>
            <td>{{ item.size || "N/A" }}</td>
            <td>
              <template v-if="item.status === 1">
                <span style="color: green">{{ $t("Custom.active") }}</span>
              </template>
              <template v-else>
                <span style="color: red">{{ $t("Custom.inactive") }}</span>
              </template>
            </td>
            <td>
              <div class="line-buttons">
                <div
                  class="action-button main"
                  @click.stop="handleAddToList(item)"
                >
                  <span class="material-symbols-outlined notification-icon">
                    list_alt_add
                  </span>
                  <span>{{ $t("List.addToList") }}</span>
                </div>
                <template v-if="item?.library?.length > 0">
                  <div
                    class="action-button green"
                    @click.stop="handleAreYouSureModal(item)"
                    title="Remove From Library"
                  >
                    <span class="material-symbols-outlined notification-icon"
                      >bookmarks</span
                    >
                    <span>Added</span>
                  </div>
                </template>
                <template v-else>
                  <div
                    class="action-button main"
                    @click="handleAddToLibrary(item)"
                    title="Add To Library"
                  >
                    <span class="material-symbols-outlined notification-icon">
                      bookmark_add
                    </span>
                    <span>Add To Library</span>
                  </div>
                </template>
              </div>
            </td>
            <td>
              <div class="action-buttons">
                <div
                  class="action-button"
                  :class="{
                    red: !item?.library[0]?.isNotification,
                    green: item?.library[0]?.isNotification,
                  }"
                  @click.stop="handleNotification(item.id, item.library, index)"
                >
                  <template v-if="loadingIndex === index">
                    <span class="button-spinner"></span>
                  </template>
                  <template v-else>
                    <span class="material-symbols-outlined notification-icon">
                      {{
                        item.library?.length > 0
                          ? "notifications_active"
                          : "notification_add"
                      }}
                    </span>
                    <span>{{ item.library[0]?.isNotification ? "Is On" : "Is Off" }}</span>
                  </template>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </TableComponent>
    </div>
  </div>

  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    @closed="!notification.show"
  />

  <AreYouSureModal
    v-if="showModal"
    :isVisible="showModal"
    :title="modalTitle"
    :message="modalMessage"
    :itemId="selectedAppId"
    :confirmButtonLabel="$t('Custom.remove')"
    :cancelButtonLabel="$t('Custom.cancel')"
    @result="handleModalResult"
  />

  <AreYouSureModal
    v-if="showDeveloperModal"
    :isVisible="showDeveloperModal"
    :title="modalTitle"
    :message="modalMessage"
    :itemId="selectedDeveloperId"
    :confirmButtonLabel="$t('Custom.remove')"
    :cancelButtonLabel="$t('Custom.cancel')"
    @result="handleDeveloperModalResult"
  />

  <Modal
    :isOpen="showImportModal"
    :modalTitle="modalTitle"
    :actionButton="unaddedApps.length"
    actionType="Add To Library"
    @close="closeModal"
    @action="handleImportToAction"
  >
    <NotFoundContent
      v-if="unaddedApps.length === 0"
      message="All apps in your library"
    />
    <div v-else>
      <div class="app-list">
        <div
          v-for="app in unaddedApps"
          :key="app.id"
          class="app-card"
          :class="{ selected: selectedApps.includes(app.id) }"
          @click="toggleSelection(app.id)"
        >
          <span class="selection-indicator">
            <span
              v-if="selectedApps.includes(app.id)"
              class="dot filled"
            ></span>
            <span v-else class="dot"></span>
          </span>
          <img :src="app.icon" alt="App Icon" class="app-icon" />
          <span class="app-name" v-html="app.name"></span>
        </div>
      </div>

      <div class="add-to-list-container" ref="listDropdown">
        <div class="select-box" @click="showListSelection = !showListSelection">
          <span v-if="selectedLists.length === 0">Select List *</span>
          <span v-else>{{ selectedLists.length }} List Selected</span>
          <span class="arrow">&#9662;</span>
        </div>

        <div v-if="showListSelection" class="list-dropdown">
          <label
            v-for="list in this.availableList"
            :key="list.id"
            class="list-item"
          >
            <input type="checkbox" :value="list.id" v-model="selectedLists" />
            {{ list.listName }}
          </label>
        </div>
      </div>
    </div>
    <p v-if="showWarning" class="warning-message">
      Please select at least one app to add to the library.
    </p>
  </Modal>

  <Modal
    :isOpen="showAddToModal"
    :modalTitle="modalTitle"
    :actionButton="appList.length"
    actionType="Add To List"
    @close="closeModal"
    @action="handleAddToListAction"
  >
    <NotFoundContent v-if="appList.length === 0" message="Not Found List" />
    <div v-else class="list-container">
      <label v-for="item in sortedAppList" :key="item.id" class="list-item">
        <input
          type="checkbox"
          :value="item.id"
          v-model="selectedAppLists"
          :disabled="isAlreadySaved(item.id)"
          @change="clearWarning"
        />
        {{ item.listName }}
        <span v-if="isAlreadySaved(item.id)" class="saved-tag">
          (Already Added)
        </span>
      </label>
    </div>

    <p v-if="showWarning" class="warning-message">
      Please select at least one list.
    </p>
  </Modal>

  <Modal
    :isOpen="showNoteModal"
    :modalTitle="modalTitle"
    :actionButton="true"
    actionType="Add Note"
    @close="closeModal"
    @action="handleModalNoteResult"
  >
    <textarea
      v-model="noteContent"
      placeholder="Write your note here..."
      class="note-textarea"
      rows="5"
      maxlength="500"
    ></textarea>
  </Modal>
</template>

<script>
import NotFoundContent from "@/components/user/common/NotFoundContent.vue";
import Loading from "@/components/user/layout/LoadingComponent.vue";
import ItemCard from "@/components/user/common/ItemCard.vue";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import AppInfo from "@/components/user/viewComponent/app/AppInfo.vue";
import FilterComponent from "@/components/user/common/FilterComponent.vue";
import Modal from "@/components/custom/modals/ModalComponent.vue";
import AreYouSureModal from "@/components/custom/modals/AreYouSureModal.vue";
import { storeImage } from "@/ultis/user/functions/storePlatform";
import {
  parseSize,
  parsePrice,
  parseRate,
} from "@/ultis/user/functions/parseValue";
import { getApp, getAppCountries, importApp } from "@/api/app.api";
import {
  removeDeveloperFromAll,
  updateNotification,
  addToLibrary as addDeveloperToLibrary,
  updateNote,
} from "@/api/developerLibrary.api";
import { updateNotification as updateAppNotification } from "@/api/appLibrary.api";
import {
  getDeveloperByStoreId,
  getDeveloperFromLibrary,
} from "@/api/developer.api";
import {
  addDeveloperToList,
  getDeveloperList,
  getSavedList,
  removeDeveloperFromList,
} from "@/api/developerList.api";
import {
  AddAppToMultipleLists,
  getAppList,
  getSavedList as getAppSavedList,
} from "@/api/appList.api";
import TableComponent from "@/components/custom/table/TableComponent.vue";
import { getPlatformList } from "@/api/list.api";
import {
  addToLibrary,
  getUnaddedApp,
  removeAppFromAll,
} from "@/api/appLibrary.api";

export default {
  name: "DeveloperInfo",
  components: {
    Loading,
    ItemCard,
    NotificationComponent,
    AppInfo,
    FilterComponent,
    Modal,
    TableComponent,
    NotFoundContent,
    AreYouSureModal,
  },
  props: {
    developerData: {
      type: Object,
      required: true,
    },
    isBack: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      storeId: this.$route.params.storeId,
      selectedAppId: null,
      selectedDeveloperId: null,
      showAddToModal: false,
      showModal: false,
      showNoteModal: false,
      noteContent: "",
      showDeveloperModal: false,
      libraryLoading: false,
      appList: [],
      appSavedList: [],
      selectedAppLists: [],
      selectedApp: null,
      availableList: [],
      unaddedApps: [],
      showListSelection: false,
      selectedLists: [],
      loading: false,
      showImportModal: false,
      isDeveloperInfoVisible: true,
      isAppInfoVisible: false,
      appData: null,
      appCountries: null,
      developerFromLibrary: [],
      isNotificationLoading: false,
      isListMenuOpen: false,
      developerList: [],
      selectedApps: [],
      developerSavedList: null,
      sortCriteria: "lastUpdated",
      sortOrder: "desc",
      showWarning: false,
      searchQuery: "",
      sortingOptions: [
        { value: "name", label: this.$t("Filter.name") },
        { value: "price", label: this.$t("Filter.price") },
        { value: "size", label: this.$t("Filter.size") },
        { value: "rating", label: this.$t("Filter.rating") },
        { value: "relasedDate", label: this.$t("Filter.relasedDate") },
        { value: "lastUpdated", label: this.$t("Filter.lastUpdated") },
        { value: "status", label: this.$t("Filter.status") },
      ],
      orderOptions: [
        { value: "asc", label: this.$t("Filter.ascending") },
        { value: "desc", label: this.$t("Filter.descending") },
      ],
      tableHeaders: [
        { key: "index", label: this.$t("Table.index") },
        { key: "icon", label: this.$t("Table.icon") },
        { key: "platform", label: this.$t("Table.platform") },
        { key: "name", label: this.$t("Table.name") },
        { key: "developer", label: this.$t("Table.developer") },
        { key: "lastUpdated", label: this.$t("Table.lastUpdated") },
        { key: "releaseDate", label: this.$t("Table.releaseDate") },
        { key: "price", label: this.$t("Table.price") },
        { key: "rate", label: this.$t("Table.rate") },
        { key: "size", label: this.$t("Table.size") },
        { key: "status", label: this.$t("Table.status") },
        { key: "action", label: this.$t("Table.actions") },
        { key: "notification", label: this.$t("Table.notification") },
      ],
      isNotificationLoading: false,
      loadingIndex: null,
    };
  },
  async created() {
    Promise.all([
      await this.getDeveloperFromLibrary(),
      await this.getSavedList(),
      await this.getPlatformList(),
      await this.fetchDeveloperList(),
    ]);
  },
  computed: {
    filteredAndSortedItems() {
      if (!this.developerData.item) return [];
      if (this.searchQuery) {
        items = this.developerData.item.filter((item) =>
          item.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }
      return this.developerData.item.sort((a, b) => {
        const modifier = this.sortOrder === "asc" ? 1 : -1;

        switch (this.sortCriteria) {
          case "name":
            return a.name.localeCompare(b.name) * modifier;
          case "price":
            return (parsePrice(a.price) - parsePrice(b.price)) * modifier;
          case "size":
            return (parseSize(a.size) - parseSize(b.size)) * modifier;
          case "rating":
            return (parseRate(a.rate) - parseRate(b.rate)) * modifier;
          case "status":
            return (parseRate(a.status) - parseRate(b.status)) * modifier;
          case "lastUpdated":
            const lastUpdatedA = new Date(a.lastUpdated).getTime();
            const lastUpdatedB = new Date(b.lastUpdated).getTime();
            if (isNaN(lastUpdatedA) || isNaN(lastUpdatedB)) return 0;
            return (lastUpdatedA - lastUpdatedB) * modifier;

          case "relasedDate":
            const releaseDateA = new Date(a.relasedDate).getTime();
            const releaseDateB = new Date(b.relasedDate).getTime();
            if (isNaN(releaseDateA) || isNaN(releaseDateB)) return 0;
            return (releaseDateA - releaseDateB) * modifier;
          default:
            return 0;
        }
      });
    },
    hasSavedList() {
      return this.developerSavedList && this.developerSavedList.length > 0;
    },
    hasSupport() {
      return (
        this.developerData.supportEmail ||
        this.developerData.supportPhoneNumber ||
        this.developerData.supportAddressText
      );
    },
    isBackVisible() {
      return (
        this.isBack && (this.isDeveloperInfoVisible || this.isAppInfoVisible)
      );
    },
    sortedAppList() {
      return [...this.appList].sort((a, b) => {
        const aSaved = this.isAlreadySaved(a.id);
        const bSaved = this.isAlreadySaved(b.id);
        if (aSaved !== bSaved) return aSaved - bSaved;
        return a.listName.localeCompare(b.listName);
      });
    },
  },
  watch: {
    developerData: {
      handler(newVal) {
        this.filteredAndSortedItems;
      },
      deep: true,
    },
  },
  methods: {
    async hanldeNoteModal() {
      this.noteContent = this.developerFromLibrary?.note || "";
      this.modalTitle = "Add Note";
      this.showNoteModal = true;
    },
    async handleModalNoteResult(result) {
      this.showNoteModal = false;
      if (result) {
        this.handleUpdateNote(this.developerData?.id);
      }
    },
    async handleUpdateNote(developerId) {
      this.loading = true;
      try {
        const response = await updateNote(developerId, this.noteContent);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.getDeveloperFromLibrary();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async handleAddDeveloperToLibrary() {
      this.libraryLoading = true;
      try {
        const response = await addDeveloperToLibrary(this.developerData?.id);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.getDeveloperFromLibrary();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.libraryLoading = false;
      }
    },
    async handleRemoveDeveloper(developerId) {
      this.loading = true;
      try {
        const response = await removeDeveloperFromAll(developerId);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.getDeveloperFromLibrary();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async handleDeveloperModalResult(result, developerId) {
      this.showDeveloperModal = false;
      if (result) {
        this.handleRemoveDeveloper(developerId);
      }
    },
    handleAreYouSureDeveloperModal() {
      this.modalTitle = this.$t("Custom.areYouSure");
      this.modalMessage =
        this.$t("Custom.doYouWantRemoveThis") +
        "<b> " +
        this.developerData?.name +
        "</b> ? " +
        this.$t("Custom.thisActionCannotBeUndone") +
        "<p style='color: red'>" +
        this.$t("Custom.deleteFromAllLists") +
        "</p> ";
      this.selectedDeveloperId = this.developerData?.id;
      this.showDeveloperModal = true;
    },
    async handleNotification(appId, library, index) {
      let isNotification = library[0]?.isNotification || 0;
      if (!library || library.length === 0) {
        isNotification = 0;
      }
      isNotification = library[0]?.isNotification || 0;
      this.loadingIndex = index;
      try {
        const response = await updateAppNotification(appId, !isNotification);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.fetchDeveloper(this.developerData.storeId);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loadingIndex = null;
      }
    },
    handleLinkClick(event, routeData) {
      const url = this.$router.resolve(routeData).href;
      if (event.ctrlKey || event.metaKey || event.button === 2) {
        window.open(url, "_blank");
        return;
      }
      this.$router.push(routeData);
    },
    async handleModalResult(result, appId) {
      this.showModal = false;
      if (result) {
        this.handleRemoveApp(appId);
      }
    },
    async handleRemoveApp(appId) {
      this.loading = true;
      try {
        const response = await removeAppFromAll(appId);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.fetchDeveloper(this.developerData.storeId);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    handleAreYouSureModal(item) {
      this.modalTitle = this.$t("Custom.areYouSure");
      this.modalMessage =
        this.$t("Custom.doYouWantRemoveThis") +
        "<b>" +
        item.name +
        "</b> ? " +
        this.$t("Custom.thisActionCannotBeUndone") +
        "<p style='color: red'>" +
        this.$t("Custom.deleteFromAllLists") +
        "</p> ";
      this.selectedAppId = item.id;
      this.showModal = true;
    },
    async handleAddToLibrary(item) {
      this.loading = true;
      try {
        const response = await addToLibrary(item.id);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.fetchDeveloper(item.developer.storeId);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchDeveloper(storeId = this.storeId) {
      try {
        if (!storeId) return;
        const response = await getDeveloperByStoreId(storeId);
        if (response.data.status) {
          Object.assign(this.developerData, response.data.data);
        }
      } catch (error) {
      } finally {
      }
    },
    async handleAddToList(item) {
      this.loading = true;
      this.selectedApp = item.itemId;
      // await this.fetchAppList(item.itemId);
      this.appList = this.availableList;
      await this.getAppSavedList(item.itemId);
      this.modalTitle = "Add To List";
      this.showAddToModal = true;
      this.loading = false;
    },
    isAlreadySaved(listId) {
      return this.appSavedList.some((saved) => saved.listId === listId);
    },
    async handleAddToListAction() {
      if (this.selectedAppLists.length === 0) {
        this.showWarning = true;
        return;
      }
      this.loading = true;
      await this.addAppToLists(this.selectedApp, this.selectedAppLists);
      this.closeModal();
      this.loading = false;
    },
    async addAppToLists(appId, listIds) {
      try {
        const response = await AddAppToMultipleLists(appId, listIds);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async fetchAppList(appId) {
      // try {
      //   const response = await getAppList(appId);
      //   if (response.data.status) {
      //     this.appList = response.data.data;
      //   } else {
      //     this.showNotification(
      //       "error",
      //       this.$t("Custom.error"),
      //       response.data.msg
      //     );
      //   }
      // } catch (error) {
      //   this.showNotification(
      //     "error",
      //     this.$t("Custom.error"),
      //     error.response.data.msg
      //   );
      // }
    },
    async getAppSavedList(appId) {
      if (this.appSavedList && this.appSavedList.length > 0) {
        return;
      }

      try {
        const response = await getAppSavedList(appId);
        if (response.data.status) {
          this.appSavedList = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    clearWarning() {
      this.showWarning = false;
    },
    async importTo() {
      this.loading = true;
      // await this.getPlatformList();
      await this.getUnaddedApp();
      this.selectedApps = this.unaddedApps.map((app) => app.id);
      this.modalTitle = "Import";
      this.showImportModal = true;
      this.loading = false;
    },
    toggleSelection(appId) {
      const index = this.selectedApps.indexOf(appId);
      if (index === -1) {
        this.selectedApps.push(appId);
      } else {
        this.selectedApps.splice(index, 1);
      }
    },
    toggleListSelection(listId) {
      const index = this.selectedLists.indexOf(listId);
      if (index === -1) {
        this.selectedLists.push(listId);
      } else {
        this.selectedLists.splice(index, 1);
      }
    },
    async handleImportToAction() {
      if (this.selectedApps.length === 0) {
        this.showWarning = true;
        return;
      }
      try {
        this.loading = true;
        const response = await importApp(this.selectedApps, this.selectedLists);
        if (response.data.status) {
          await this.fetchDeveloper(this.developerData.storeId);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.closeModal();
      }
    },
    openLink(link) {
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
      }
    },
    getStoreImage(platformTypeId) {
      return storeImage(platformTypeId);
    },
    getItemIcon(icon) {
      return icon || require("@/assets/img/developer/developer.png");
    },
    formatDate(date) {
      if (!date) return "";
      const locale = localStorage.getItem("localeTime") || "en-US";
      return new Date(date).toLocaleDateString(locale);
    },
    platformTypeName(platformTypeId) {
      return platformTypeId === 1 ? "App Store" : "Play Store";
    },
    getStoreImage(platformTypeId) {
      return storeImage(platformTypeId);
    },
    getItemIcon(icon) {
      if (icon) {
        return icon;
      } else {
        return require("@/assets/img/developer/developer.png");
      }
    },
    async getUnaddedApp() {
      try {
        const response = await getUnaddedApp(this.developerData.id);
        if (response.data.status) {
          this.unaddedApps = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async handleShow(storeId) {
      this.$router.push({
        name: "App",
        params: { storeId: storeId },
      });
    },
    async getAppCountry() {
      try {
        const payload =
          this.appData.country.countryShortName === "US"
            ? { appId: this.appData.app.id }
            : { appId: this.appData.app.appId };

        const response = await getAppCountries(payload.appId);

        if (response.data.status) {
          this.appCountries = response.data.data.map((item) => ({
            ...item.country,
            appUrl: item.appUrl,
          }));
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async processAppLink(url) {
      try {
        const response = await getApp(url);

        if (response.data.status) {
          this.appData = response.data.data;
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
      }
    },
    async fetchData(url = this.url) {
      this.loading = true;
      try {
        await this.processAppLink(url);
        await this.getAppCountry();
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async updateNotification() {
      this.isNotificationLoading = true;
      const developerIsNotification = this.developerFromLibrary?.isNotification
        ? false
        : true;
      try {
        const response = await updateNotification(
          this.developerData.id,
          developerIsNotification
        );
        if (response.data.status) {
          await this.getDeveloperFromLibrary();
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.isNotificationLoading = false;
      }
    },
    async getDeveloperFromLibrary() {
      const payload = { developerId: this.developerData.id };
      try {
        const response = await getDeveloperFromLibrary(payload.developerId);
        if (response.data.status) {
          this.developerFromLibrary = response.data.data;
        } else {
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async fetchDeveloperList() {
      const payload = { developerId: this.developerData.id };
      try {
        const response = await getDeveloperList(payload.developerId);
        if (response.data.status) {
          this.developerList = response.data.data;
        } else {
          console.error("response.data.msgs", response.data.msg);
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async getSavedList() {
      if (this.developerSavedList && this.developerSavedList.length > 0) {
        return;
      }
      const payload = { developerId: this.developerData.id };

      try {
        const response = await getSavedList(payload.developerId);
        if (response.data.status) {
          this.developerSavedList = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async setList(listId, isChecked) {
      this.loading = true;
      const payload = { developerId: this.developerData.id, listId: listId };

      if (isChecked) {
        await this.addDeveloperToList(payload);
      } else {
        await this.removeDeveloperFromList(payload);
      }

      this.loading = false;
    },
    async addDeveloperToList(payload) {
      try {
        const response = await addDeveloperToList(
          payload.developerId,
          payload.listId
        );
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.getSavedList();
          await this.fetchDeveloperList();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async removeDeveloperFromList(payload) {
      try {
        const response = await removeDeveloperFromList(
          payload.developerId,
          payload.listId
        );
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.getSavedList();
          await this.fetchDeveloperList();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async getPlatformList() {
      try {
        const response = await getPlatformList(
          this.developerData.platformType.id
        );
        if (response.data.status) {
          this.availableList = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    isChecked(listId) {
      if (!this.hasSavedList) return false;
      return this.developerSavedList.some((target) => target.listId === listId);
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
    toggleDropdown() {
      this.isListMenuOpen = !this.isListMenuOpen;
    },
    handleFilterChange({ criteria, order }) {
      this.sortCriteria = criteria;
      this.sortOrder = order;
    },
    handleBack() {
      if (this.isAppInfoVisible) {
        this.closeAppInfo();
      } else if (this.isDeveloperInfoVisible) {
        this.closeDeveloperInfo();
      }
    },
    closeDeveloperInfo() {
      this.isDeveloperInfoVisible = false;
      this.$emit("close");
    },
    closeAppInfo() {
      this.isAppInfoVisible = false;
      this.isDeveloperInfoVisible = true;
    },
    closeModal() {
      this.selectedLists = [];
      this.selectedApps = [];
      this.selectedAppLists = [];
      this.appList = [];
      this.appSavedList = [];
      this.selectedApp = null;
      this.showAddToModal = false;
      this.showNoteModal = false;
      this.showImportModal = false;
    },
    handleClickOutside(event) {
      if (
        this.$refs.listDropdown &&
        !this.$refs.listDropdown.contains(event.target)
      ) {
        this.isListMenuOpen = false;
        this.showListSelection = false;
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.developer-note-container {
  margin-top: 20px;
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.note-icon-area {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.note-preview {
  margin-left: 10px;
  color: #555;
}

.note-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
}

.filter-container {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.import-btn {
  background: var(--logo-color);
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  margin-right: 5px;
  gap: 5px;
  cursor: pointer;
  transition: background 0.3s;
  font-size: 14px;
}

.import-btn:hover {
  background: var(--logo-hover-color);
}

.import-btn span {
  font-size: 18px;
}

@media (max-width: 768px) {
  .import-btn {
    width: 100%;
    justify-content: center;
  }
}

/** */
.add-to-list-container {
  position: relative;
  width: 100%;
}

.select-box {
  background: white;
  border: 1px solid #ccc;
  padding: 12px 15px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 250px;
  font-size: 14px;
  font-weight: 500;
  position: relative;
  width: 100%;
}

.list-dropdown {
  position: absolute;
  width: 100%;
  background: white;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  max-height: 250px;
  overflow-y: auto;
  padding: 12px;
  z-index: 10000;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  font-size: 14px;
}

.list-item:hover {
  cursor: pointer;
}

.list-item input {
  cursor: pointer;
  transform: scale(1.2);
}

.warning-message {
  color: red;
  font-size: 0.8rem;
  margin-top: 10px;
  text-align: center;
}

/** */
.app-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  margin-bottom: 15px;
}

.app-card {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px solid #ccc;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 5px;
  cursor: pointer;
  transition: border 0.3s;
  position: relative;
  /* width: calc(50% - 10px); */
}

.app-card.selected {
  border-color: var(--logo-color);
}

.selection-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}

.dot {
  width: 12px;
  height: 12px;
  border: 2px solid #666;
  border-radius: 50%;
  display: inline-block;
  transition: all 0.3s ease;
}

.filled {
  background-color: var(--logo-color);
  border-color: white;
  outline: 1px solid #666;
}

.app-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
}

.app-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

@media (max-width: 600px) {
  .app-card {
    width: 100%;
  }
}

.developer-wrapper {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.developer-cover {
  position: relative;
  height: 300px;
  border-radius: 12px 12px 0px 0px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.developer-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
}

.developer-main-info {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
}

.developer-icon {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  flex-shrink: 0;
}

.developer-icon.no-icon {
  background: #fff6ee;
  color: var(--logo-color);
}

.developer-icon img {
  width: 100%;
  height: 100%;
  border-radius: 16px;
  object-fit: cover;
}

.developer-primary {
  flex-grow: 1;
}

.developer-name {
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
}

.platform-badge {
  display: inline-flex;
  align-items: center;
  background: #f3f4f6;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  color: #4b5563;
  gap: 6px;
}

.developer-links {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.link-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f9fafb;
  border-radius: 8px;
  color: #4b5563;
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 14px;
}

.link-item:hover {
  background: #f3f4f6;
  color: #111827;
}

.link-item.store-link {
  background: var(--logo-color);
  color: white;
}

.link-item.store-link:hover {
  background: var(--logo-hover-color);
}

/* .link-item .material-symbols-outlined {
  font-size: 18px;
} */

.developer-support {
  border-top: 1px solid #e5e7eb;
  padding-top: 24px;
}

.support-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
}

.support-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.support-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #4b5563;
}
.support-item a {
  color: var(--logo-color);
  text-decoration: none;
  transition: color 0.2s ease;
}

.support-item a:hover {
  color: var(--logo-color);
  text-decoration: underline;
}

.support-item.address {
  grid-column: 1 / -1;
}

.address-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.map-link {
  font-size: 14px;
}

.apps-badge {
  display: flex;
  padding: 15px 0px;
}

@media (max-width: 640px) {
  .developer-header {
    padding: 16px;
  }

  .developer-main-info {
    gap: 16px;
  }

  .developer-icon {
    width: 35px;
    height: 35px;
  }

  .developer-name {
    font-size: 20px;
  }

  .developer-links {
    flex-direction: column;
  }

  .support-grid {
    grid-template-columns: 1fr;
  }
  .developer-cover {
    height: 150px;
  }
}
/** */
.properties-strip {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 10px;
}

.material-icons-outlined {
  font-size: 15px;
}

.currency-switcher {
  position: relative;
  margin: 0 auto;
  width: 268px;
}
.flag {
  width: 25px;
}

div.dropdown {
  position: relative;
  width: 190px;
}

div.dropdown:after {
  content: "";
  position: absolute;
  margin: 0;
  width: 16px;
  height: 17px;
  background-image: url(http://botkits.ru/img/icons/down.svg);
  top: 15px;
  right: 10px;
  -webkit-transition: 0.3s;
  transition: 0.3s;
}

div.dropdown.open:after {
  -webkit-transform: rotate(180deg);
  transform: rotate(180deg);
}

div.dropdown > div.caption {
  background: #ffffff;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  padding: 9px 13px 9px 45px;
  font-size: 13px;
  line-height: 150%;
  letter-spacing: 0.3px;
  box-shadow: 0 0 5px #9e9e9e;
}

div.dropdown > div.list {
  position: absolute;
  background-color: #fff;
  width: 100%;
  top: 42px;
  border-radius: 8px;
  padding: 10px 0;
  opacity: 0;
  visibility: hidden;
  overflow: hidden;
  overflow-y: auto;
  max-height: 300px;
  box-shadow: 0px 12px 24px rgba(21, 18, 51, 0.13);
  transform: scale(0.85);
  transform-origin: 50% 0;
  transition: all 0.15s cubic-bezier(0.25, 0, 0.25, 1.75), opacity 0.1s linear;
  -webkit-transition: all 0.15s cubic-bezier(0.25, 0, 0.25, 1.75),
    opacity 0.1s linear;
  -webkit-box-shadow: 0px 12px 24px rgba(21, 18, 51, 0.13);
  -webkit-transform: scale(0.85);
  -webkit-transform-origin: 50% 0;
  z-index: -1;
}

div.dropdown > div.list::-webkit-scrollbar {
  width: 4px;
  cursor: pointer;
}

div.dropdown > div.list::-webkit-scrollbar-thumb {
  background-color: var(--logo-color);
  border-radius: 4px;
  cursor: pointer;
}

div.dropdown > div.list::-webkit-scrollbar-thumb:hover {
  background-color: var(--logo-hover-color);
  cursor: pointer;
}

div.dropdown.open > div.list {
  -webkit-transform: scale(1);
  transform: scale(1);
  opacity: 1;
  z-index: 1;
  visibility: visible;
}

div.dropdown > div.list > div.item {
  padding: 10.5px 15px 10.5px 62px;
  cursor: pointer;
  -webkit-transition: 0.3s;
  transition: 0.3s;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.3px;
}

div.dropdown > div.list > div.item.selected {
  background: rgba(36, 60, 187, 0.2);
  pointer-events: none;
}

div.dropdown > div.list > div.item:hover {
  background: #f8f9fb;
}

div.dropdown > div.caption img,
div.dropdown > div.list > div.item img,
div.dropdown > div.caption svg,
div.dropdown > div.list > div.item svg {
  position: absolute;
  margin-top: 2.5px;
  left: 15px;
}

div.dropdown > div.list > div.item span,
div.dropdown > div.caption span {
  font-weight: 600;
  font-size: 10px;
  letter-spacing: 0.3px;
  color: var(--logo-color);
  position: absolute;
  right: 20px;
}

div.dropdown > div.list > div.item span {
  right: 12px;
}
div.dropdown > div.caption .list-icon {
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
  color: var(--logo-color);
  transition: transform 0.2s ease;
}
.list-menu {
  margin-top: 10px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 0 5px #9e9e9e;
  padding: 10px;
  max-width: 300px;
  width: 100%;
}

.checkbox-item {
  display: flex;
  align-items: center;
  margin-bottom: 0px;
  padding: 0px 15px;
}

.checkbox-item input[type="checkbox"] {
  appearance: none;
  width: 16px;
  height: 16px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  display: inline-block;
  margin-right: 5px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  background-color: white;
}

.checkbox-item input[type="checkbox"]:hover,
.checkbox-item:hover input[type="checkbox"] {
  border-color: var(--logo-color);
  box-shadow: 0 0 5px rgba(243, 177, 33, 0.4);
}

.checkbox-item input[type="checkbox"]:checked {
  border-color: var(--logo-color);
  background-color: var(--logo-color);
}

.checkbox-item input[type="checkbox"]:checked::after {
  content: "\2713";
  font-size: 14px;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.checkbox-item label {
  font-size: 14px;
  margin-left: 2px;
  color: #333;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 40px);
}

.checkbox-item:hover {
  cursor: pointer;
}

.list-item {
  display: flex;
  align-items: center;
  min-width: 100%;
  padding: 8px 0px;
  margin-bottom: 5px;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.icon-button-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 15px;
  cursor: pointer;
  background: white;
  border-radius: 8px;
  box-shadow: 0 0 5px #9e9e9e;
  position: relative;
  height: 40px;
}

.icon-button-container .text {
  font-size: 13px;
  margin-left: 8px;
  font-weight: 500;
  color: black;
}

.icon-button-container .icon {
  font-size: 16px;
}

@media (max-width: 855px) {
  div.dropdown {
    position: relative;
    width: 100%;
  }
}

</style>
